import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { UserService } from '../../services/user.service';
import { CommonModule, NgForOf } from '@angular/common';
import { environment } from '../../../environments/environment';
import { TuiAlertService, TuiAppearance, TuiButton, TuiIcon } from '@taiga-ui/core';
import { TuiButtonGroup, TuiSkeleton } from '@taiga-ui/kit';
import { questionsStart } from '../../const/question-start';
import { QuestionStart } from '../../inteface';
import { TuiLegendItem, TuiRingChart } from '@taiga-ui/addon-charts';
import { TuiHovered } from '@taiga-ui/cdk';
import { Router } from '@angular/router';
import { ROUTES_CONST } from '../../const/menu';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { MetrikaService } from '../../services/metrika.service';

@Component({
  selector: 'app-start',
  standalone: true,
  imports: [
    CommonModule,
    TuiAppearance,
    TuiButton,
    TuiButtonGroup,
    TuiIcon,
    NgForOf,
    TuiHovered,
    TuiLegendItem,
    TuiRingChart,
    TuiSkeleton,
    TranslocoDirective,
  ],
  templateUrl: './start.component.html',
  styleUrl: './start.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StartComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly alerts = inject(TuiAlertService);
  private readonly metrika = inject(MetrikaService);
  protected readonly router = inject(Router);
  environment = environment;
  ROUTES_CONST = ROUTES_CONST;
  totalPoints: number = 0;
  skeleton = true;
  categoryPersonDefault = {};
  categoryPerson = {
    Parent: { key: 'Parent', name: 'Родитель', count: 0, percent: 0 },
    Adult: { key: 'Adult', name: 'Взрослый', count: 0, percent: 0 },
    Child: { key: 'Child', name: 'Ребенок', count: 0, percent: 0 },
    Balance: { key: 'Balance', name: 'Баланс', count: 0, percent: 100 },
  };
  step: number = -1;
  testCompleted = false;

  protected activeItemIndex = NaN;

  value = [
    this.categoryPerson.Parent.percent,
    this.categoryPerson.Adult.percent,
    this.categoryPerson.Child.percent,
  ];
  protected readonly labels = [
    this.categoryPerson.Parent.name,
    this.categoryPerson.Adult.name,
    this.categoryPerson.Child.name,
  ];

  protected isItemActive(index: number): boolean {
    return this.activeItemIndex === index;
  }

  protected onHover(index: number, hovered: boolean): void {
    this.activeItemIndex = hovered ? index : NaN;
  }

  questions: QuestionStart[] = questionsStart;

  answerTypes = [
    { count: 1, title: 'Нет', img: '@tui.angry' },
    { count: 2, title: '', img: '@tui.annoyed' },
    { count: 3, title: '', img: '@tui.meh' },
    { count: 4, title: '', img: '@tui.smile' },
    { count: 5, title: 'Да', img: '@tui.smile-plus' },
  ];

  constructor(private translocoService: TranslocoService) {
    const activeLang = translocoService.getActiveLang();
    console.log(activeLang);
    translocoService.load(activeLang).subscribe(() => {
      console.log('Language loaded successfully');
      const trlt = this.translocoService.getTranslation(activeLang);
      console.log(trlt);
      // Обновляем названия категорий
      this.categoryPerson.Parent.name = trlt['start.results.parentDominant'];
      this.categoryPerson.Adult.name = trlt['start.results.adultDominant'];
      this.categoryPerson.Child.name = trlt['start.results.childDominant'];

      // Обновляем ответы
      this.answerTypes[0].title = trlt['start.answers.no'];
      this.answerTypes[4].title = trlt['start.answers.yes'];

      // Обновляем labels для графика
      this.labels[0] = this.categoryPerson.Parent.name;
      this.labels[1] = this.categoryPerson.Adult.name;
      this.labels[2] = this.categoryPerson.Child.name;
    });
  }

  ngOnInit(): void {
    Object.assign(this.categoryPersonDefault, this.categoryPerson);
    this.shuffleQuestions();
  }

  get resultTest() {
    if (
      this.categoryPerson.Parent.count > this.categoryPerson.Adult.count &&
      this.categoryPerson.Parent.count > this.categoryPerson.Child.count
    ) {
      return this.categoryPerson.Parent;
    } else if (
      this.categoryPerson.Adult.count > this.categoryPerson.Parent.count &&
      this.categoryPerson.Adult.count > this.categoryPerson.Child.count
    ) {
      return this.categoryPerson.Adult;
    } else if (
      this.categoryPerson.Child.count > this.categoryPerson.Parent.count &&
      this.categoryPerson.Child.count > this.categoryPerson.Adult.count
    ) {
      return this.categoryPerson.Child;
    }
    return this.categoryPerson.Balance;
  }

  private shuffleQuestions(): void {
    for (let i = this.questions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.questions[i], this.questions[j]] = [
        this.questions[j],
        this.questions[i],
      ];
    }
  }

  onImageLoad(index?: number): void {
    if (index !== undefined) {
      this.questions[index].skeleton = false;
    } else {
      this.skeleton = false;
    }
  }

  startTest(): void {
    this.step = this.step + 1;
    this.metrika.reachGoal('test_start');
  }

  answer(index, score): void {
    this.questions[index].score = score;
    this.step = this.step + 1;
    if (this.step == this.questions.length) {
      this.submitTest();
    }
  }

  async shareResult() {
    this.metrika.reachGoal('test_share', { dominant: this.resultTest.key });
    const nameFile = this.resultTest.key.toLowerCase() + '.html';
    const url = `https://${this.userService.user.lang}.${environment.resultUrl}/results/${nameFile}?result=${Math.floor(
      Math.random() * 1000000000,
    )}`;
    const shareData: ShareData = {
      url,
      title: this.translocoService.translate('start.actions.shareTitle'),
    };

    if (typeof navigator.share === 'function') {
      const canShare = !navigator.canShare || navigator.canShare(shareData);
      if (canShare) {
        try {
          await navigator.share(shareData);
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            console.log('Ошибка при шаринге:', error);
            await this.fallbackCopyToClipboard(url);
          }
        }
      } else {
        await this.fallbackCopyToClipboard(url);
      }
    } else {
      await this.fallbackCopyToClipboard(url);
    }
  }

  private async fallbackCopyToClipboard(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      const message = this.translocoService.translate(
        'start.actions.linkCopied',
      );
      this.alerts.open(message, { appearance: 'success', autoClose: 3000 }).subscribe();
    } catch {
      console.log('Не удалось скопировать ссылку');
    }
  }

  // getFacebookShareUrl() {
  //   return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
  //     window.location.href
  //   )}&quote=${encodeURIComponent(this.resultText)}`;
  // }

  // getTwitterShareUrl() {
  //   return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
  //     this.resultText
  //   )}&url=${encodeURIComponent(window.location.href)}`;
  // }

  // getTelegramShareUrl() {
  //   return `https://t.me/share/url?url=${encodeURIComponent(
  //     window.location.href
  //   )}&text=${encodeURIComponent(this.resultText)}`;
  // }

  // getWhatsAppShareUrl() {
  //   return `https://wa.me/?text=${encodeURIComponent(this.resultText)}`;
  // }

  submitTest() {
    this.questions.forEach((q) => {
      if (q.score) {
        this.categoryPerson[q.category].count += q.score;
      }
    });

    this.totalPoints =
      this.categoryPerson.Parent.count +
      this.categoryPerson.Adult.count +
      this.categoryPerson.Child.count;
    this.categoryPerson.Parent.percent = Math.round(
      (this.categoryPerson.Parent.count / this.totalPoints) * 100,
    );
    this.categoryPerson.Adult.percent = Math.round(
      (this.categoryPerson.Adult.count / this.totalPoints) * 100,
    );
    this.categoryPerson.Child.percent = Math.round(
      (this.categoryPerson.Child.count / this.totalPoints) * 100,
    );

    this.value = [
      this.categoryPerson.Parent.percent,
      this.categoryPerson.Adult.percent,
      this.categoryPerson.Child.percent,
    ];

    this.testCompleted = true;
    this.step = 0;

    // Главная цель кампаний Директа: пользователь дошёл до экрана результата.
    this.metrika.reachGoal('test_complete', {
      dominant: this.resultTest.key,
      parent: this.categoryPerson.Parent.percent,
      adult: this.categoryPerson.Adult.percent,
      child: this.categoryPerson.Child.percent,
    });
  }

  reloadTest() {
    this.shuffleQuestions();
    this.testCompleted = false;
    Object.assign(this.categoryPerson, this.categoryPersonDefault);
  }
}
